import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  type SelectChangeEvent,
  Stack,
  TextField,
  Toolbar,
  Typography,
} from '@mui/material';
import ConfirmIcon from '@mui/icons-material/CheckCircleOutline';
import FalsePositiveIcon from '@mui/icons-material/ThumbDownAltOutlined';
import { useTranslation } from 'react-i18next';
import { FALSE_POSITIVE_REASONS, type BulkTriage } from '@components/findings/useBulkTriage';
import type { FalsePositiveReason } from '@services/findingsService';

/**
 * REV-220/REV-509, extracted for REV-720: the toolbar, the result message and
 * the false-positive dialog that go with `useBulkTriage`.
 *
 * Purely presentational — every decision lives in the hook. That split is what
 * lets the scan-run view offer the same actions as `/findings` rather than a
 * second implementation of them, which is the thing the ticket is about.
 *
 * Render it above the table.
 *
 * The prop is the hook's return MINUS `selection`, which belongs to the table
 * and which this component never reads. Taking the whole object let a test
 * fixture pair `selection: undefined` with `selectedCount: 2` — a state the
 * hook has no path to, since `selection` is only undefined when there is no
 * organization and nothing can then be selected. Narrowing the type makes that
 * combination unrepresentable rather than merely unused.
 */
export type BulkTriageBarState = Omit<BulkTriage, 'selection'>;

export default function BulkTriageBar({ bulk }: { bulk: BulkTriageBarState }) {
  const { t } = useTranslation();

  return (
    <>
      {bulk.toast && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={bulk.dismissToast} role="status">
          {bulk.toast}
        </Alert>
      )}

      {bulk.selectedCount > 0 && (
        <Paper variant="outlined" sx={{ mb: 2 }}>
          <Toolbar
            role="toolbar"
            aria-label={t('findings.bulk.toolbarLabel', 'Bulk actions')}
            sx={{ gap: 1, flexWrap: 'wrap', bgcolor: 'action.selected', borderRadius: 1 }}
          >
            {/* Polite, not assertive: the count changes with every tick, and a
                screen-reader user ticking boxes should hear it between actions
                rather than be interrupted mid-row. */}
            <Typography sx={{ flex: '0 0 auto', mr: 2 }} aria-live="polite">
              {t('findings.bulk.selectedCount', {
                defaultValue: '{{count}} selected',
                count: bulk.selectedCount,
              })}
            </Typography>
            <Button
              size="small"
              variant="contained"
              color="success"
              startIcon={<ConfirmIcon />}
              onClick={() => void bulk.confirm()}
              disabled={bulk.submitting}
            >
              {t('findings.bulk.confirm', 'Confirm')}
            </Button>
            <Button
              size="small"
              variant="outlined"
              color="error"
              startIcon={<FalsePositiveIcon />}
              onClick={bulk.openFp}
              disabled={bulk.submitting}
            >
              {t('findings.bulk.falsePositive', 'Mark false positive')}
            </Button>
            <Button size="small" onClick={bulk.clear} disabled={bulk.submitting}>
              {t('findings.bulk.clear', 'Clear selection')}
            </Button>
          </Toolbar>
        </Paper>
      )}

      <Dialog open={bulk.fpOpen} onClose={bulk.closeFp} fullWidth maxWidth="sm">
        <DialogTitle>
          {t('findings.bulk.fpDialog.title', {
            defaultValue: 'Mark {{count}} finding(s) as false positive',
            count: bulk.selectedCount,
          })}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {bulk.fpError && <Alert severity="error">{bulk.fpError}</Alert>}
            <Typography variant="body2" color="text.secondary">
              {t(
                'findings.bulk.fpDialog.help',
                'Use this when the findings themselves are wrong. If they are real but will not be fixed, mark them as won’t fix instead.'
              )}
            </Typography>
            <FormControl fullWidth>
              <InputLabel id="bulk-fp-reason-label">
                {t('findings.bulk.fpDialog.reason', 'Reason')}
              </InputLabel>
              <Select
                labelId="bulk-fp-reason-label"
                id="bulk-fp-reason"
                value={bulk.fpReason}
                label={t('findings.bulk.fpDialog.reason', 'Reason')}
                onChange={(e: SelectChangeEvent) =>
                  bulk.setFpReason(e.target.value as FalsePositiveReason)
                }
              >
                {FALSE_POSITIVE_REASONS.map((r) => (
                  <MenuItem key={r} value={r}>
                    {t(`findingDetail.fpReasons.${r}`, r.replace(/_/g, ' '))}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label={t('findings.bulk.fpDialog.notes', 'Notes')}
              value={bulk.fpNotes}
              onChange={(e) => bulk.setFpNotes(e.target.value)}
              required
              error={Boolean(bulk.fpError)}
              helperText={t(
                'findings.bulk.fpDialog.notesHelp',
                'Required. Explain why these findings are incorrect.'
              )}
              multiline
              rows={3}
              fullWidth
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={bulk.closeFp} disabled={bulk.submitting}>
            {t('common.cancel', 'Cancel')}
          </Button>
          {/* Never pre-disabled on validity — only while the request is in
              flight. The notes rule is enforced on submit, where it can say
              what is missing. */}
          <Button
            variant="contained"
            color="error"
            onClick={() => void bulk.markFalsePositive()}
            disabled={bulk.submitting}
          >
            {bulk.submitting
              ? t('common.saving', 'Saving…')
              : t('findings.bulk.fpDialog.submit', {
                  defaultValue: 'Mark {{count}} as false positive',
                  count: bulk.selectedCount,
                })}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
